import { Button, Input, notify } from '@affine/component';
import { FetchService } from '@affine/core/modules/cloud';
import { IntegrationService } from '@affine/core/modules/integration';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import {
  IntegrationSettingHeader,
  IntegrationSettingItem,
} from '../setting';
import * as styles from './setting-panel.css';

export const DevtoSettingPanel = () => {
  const devto = useService(IntegrationService).devto;
  const settings = useLiveData(devto.settings$);
  const apiKey = settings?.apiKey;

  return apiKey ? <DevtoConnectedSetting /> : <DevtoNotConnectedSetting />;
};

const DevtoSettingHeader = ({ action }: { action?: React.ReactNode }) => {
  return (
    <IntegrationSettingHeader
      icon={
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M7.42 10.05c-.18-.16-.46-.23-.84-.23H6v4.36h.58c.37 0 .65-.08.84-.23.18-.16.27-.45.27-.89v-1.13c0-.43-.09-.72-.27-.88zm.25 2.23c0 .29-.04.51-.12.65-.08.14-.21.23-.39.27-.08.02-.17.02-.28.02H6.8v-3.15h.58c.25 0 .43.06.54.18.11.12.17.34.17.67v1.36zM10.2 14.18V9.82h1.44v.44h-.96v1.36h.93v.44h-.93v1.68h.96v.44H10.2zm2.13-4.36h.56l.58 2.7h.02l.58-2.7h.56L13.5 14.18h-.54l-.63-4.36zm4.87.46c-.2-.17-.47-.26-.81-.26-.35 0-.62.09-.82.26-.2.17-.3.44-.3.8v2.05c0 .36.1.62.3.8.2.17.47.26.82.26.34 0 .61-.09.81-.26.2-.18.3-.44.3-.8V11.08c0-.36-.1-.63-.3-.8zm-.18 2.88c0 .18-.04.31-.12.41-.08.1-.2.15-.36.15-.16 0-.28-.05-.36-.15-.08-.1-.12-.23-.12-.41V11.05c0-.18.04-.31.12-.41.08-.1.2-.15.36-.15.16 0 .28.05.36.15.08.1.12.23.12.41v2.11zM0 4.85v14.3c0 2.68 2.17 4.85 4.85 4.85h14.3c2.68 0 4.85-2.17 4.85-4.85V4.85C24 2.17 21.83 0 19.15 0H4.85C2.17 0 0 2.17 0 4.85zM19.65 12c0 4.23-3.43 7.65-7.65 7.65-4.23 0-7.65-3.43-7.65-7.65 0-4.23 3.43-7.65 7.65-7.65 4.23 0 7.65 3.43 7.65 7.65z"/>
        </svg>
      }
      name="Dev.to"
      desc="Publish your documents directly to Dev.to, the community for developers."
      action={action}
    />
  );
};

const DevtoNotConnectedSetting = () => {
  const devto = useService(IntegrationService).devto;
  const fetchService = useService(FetchService);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = useCallback(async () => {
    if (!apiKey.trim()) {
      notify.error({ title: 'Please enter your API key' });
      return;
    }

    setLoading(true);
    try {
      // Validate the API key by making a test request through our backend
      const response = await fetchService.fetch('/api/devto/me', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ apiKey: apiKey.trim() }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Invalid API key');
      }

      const data = await response.json();
      const user = data.user;
      devto.connect(apiKey.trim());
      notify.success({
        title: 'Connected to Dev.to',
        message: `Signed in as ${user.name || user.username}`,
      });
    } catch (error) {
      notify.error({
        title: 'Failed to connect',
        message: error instanceof Error ? error.message : 'Please check your API key and try again',
      });
    } finally {
      setLoading(false);
    }
  }, [apiKey, devto, fetchService]);

  return (
    <div>
      <DevtoSettingHeader />
      <div className={styles.connectSection}>
        <IntegrationSettingItem
          name="API Key"
          desc="Enter your Dev.to API key to enable publishing"
        >
          <div className={styles.inputWrapper}>
            <Input
              type="password"
              value={apiKey}
              onChange={setApiKey}
              placeholder="Enter your API key"
              disabled={loading}
            />
          </div>
        </IntegrationSettingItem>
        <div className={styles.hint}>
          Generate an API key at{' '}
          <a
            href="https://dev.to/settings/extensions"
            target="_blank"
            rel="noopener noreferrer"
          >
            dev.to/settings/extensions
          </a>
        </div>
        <Button
          variant="primary"
          size="large"
          onClick={handleConnect}
          disabled={loading || !apiKey.trim()}
          className={styles.connectButton}
        >
          {loading ? 'Connecting...' : 'Connect'}
        </Button>
      </div>
    </div>
  );
};

const DevtoConnectedSetting = () => {
  const devto = useService(IntegrationService).devto;
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = useCallback(() => {
    setDisconnecting(true);
    devto.disconnect();
    notify.success({ title: 'Disconnected from Dev.to' });
    setDisconnecting(false);
  }, [devto]);

  return (
    <div>
      <DevtoSettingHeader
        action={
          <Button
            variant="error"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            Disconnect
          </Button>
        }
      />
      <div className={styles.connectedSection}>
        <div className={styles.connectedMessage}>
          Your Dev.to account is connected. You can now publish documents
          directly to Dev.to from the document menu.
        </div>
      </div>
    </div>
  );
};
